import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { setUnauthorizedHandler } from "@teamna/web-client/api/client.ts";
import { createQueryClient } from "@teamna/web-client/lib/query-client.ts";
import { fullAppHref, isStale, reloadOnStaleChunk, startVersionChecks } from "@teamna/ui/lib/version-check.ts";
import { guardDomAgainstTranslation } from "@teamna/ui/lib/translate-guard.ts";
import { configureControlCenter, onSessionEnded } from "./lib/configure.ts";
import { createCcRouter } from "./router.tsx";
import "@teamna/ui/styles.css";

// Chrome translate must not crash a screen (see lib/translate-guard.ts). Before the first render.
guardDomAgainstTranslation();

/**
 * Boot. The shared client goes to `/v1/desk` with the act-as header and the session cookie; a 401
 * from anywhere means the staff session ended, so the cache is dropped and the page goes to
 * sign-in.
 */
configureControlCenter();

const queryClient = createQueryClient();
const router = createCcRouter();

setUnauthorizedHandler(() => onSessionEnded(queryClient, router));

router.subscribe("onBeforeNavigate", ({ toLocation, pathChanged }) => {
  if (pathChanged && isStale()) window.location.assign(fullAppHref(toLocation.href));
});
startVersionChecks();
reloadOnStaleChunk();

const container = document.getElementById("root");
if (!container) throw new Error("index.html is missing #root");

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
